import { useFormContext, useWatch } from "react-hook-form";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { communityConfigurationCardSx } from "./communityConfigurationStyles";

import type {
  CommunityConfiguration,
  CommunityConfigurationIssue,
} from "@graphql/api";
import type { CommunityConfigurationFormValues } from "./communitiesConfigurationForm";

interface CommunityConfigurationSummaryProps {
  backendIssues: CommunityConfigurationIssue[];
  clientErrors: string[];
  portfolio: CommunityConfiguration["portfolio"];
}

export function CommunityConfigurationSummary({
  backendIssues,
  clientErrors,
  portfolio,
}: CommunityConfigurationSummaryProps) {
  const { control } = useFormContext<CommunityConfigurationFormValues>();
  const communities = useWatch({ control, name: "communities" }) ?? [];
  const activeCount = communities.filter(({ isActive }) => isActive).length;
  const incompleteCount = communities.filter(
    ({ ctmhcId, isActive, ownerClientId }) =>
      isActive && (!ctmhcId?.trim() || !ownerClientId)
  ).length;

  return (
    <>
      <Stack sx={communityConfigurationCardSx} spacing={2}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          gap={2}>
          <Box>
            <Typography variant="h3">Community configuration</Typography>
            <Typography color="text.secondary">
              {portfolio.name} · {portfolio.integrationType}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip label={`${communities.length} communities`} />
            <Chip color="primary" label={`${activeCount} active`} />
            <Chip label={`${communities.length - activeCount} inactive`} />
            <Chip
              color={incompleteCount ? "warning" : "primary"}
              label={`${incompleteCount} incomplete active`}
            />
          </Stack>
        </Stack>
        <Alert severity="info">
          Policy numbers are required once for each state and legal entity owner
          combination. Pricing, fees, and limits apply to the entire portfolio.
        </Alert>
      </Stack>

      {(clientErrors.length > 0 || backendIssues.length > 0) && (
        <Alert severity="error">
          <Typography fontWeight={600}>
            Configuration needs attention:
          </Typography>
          <Box component="ul" my={0} pl={3}>
            {clientErrors.map((message) => (
              <li key={message}>{message}</li>
            ))}
            {backendIssues.map((issue) => (
              <li key={`${issue.path}-${issue.code}`}>{issue.message}</li>
            ))}
          </Box>
        </Alert>
      )}
    </>
  );
}
